import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../contexts/AuthContext';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Eye, EyeOff, Mail, Lock, User, Building, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const signupSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Invalid email address'),
  password: z.string()
    .min(12, 'Password must be at least 12 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number')
    .regex(/[^A-Za-z0-9]/, 'Password must contain at least one special character'),
  confirmPassword: z.string(),
  accountType: z.enum(['individual', 'company'], {
    required_error: 'Please select an account type',
  }),
  acceptTerms: z.boolean().refine((val) => val === true, {
    message: 'You must accept the Privacy Policy',
  }),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Passwords do not match',
  path: ['confirmPassword'],
});

type SignupFormData = z.infer<typeof signupSchema>;

interface SignupFormProps {
  onToggleMode: () => void;
  onClose: () => void;
}

const SignupForm: React.FC<SignupFormProps> = ({ onToggleMode, onClose }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const { signup, signInWithGoogle, isLoading } = useAuth();
  const { t } = useTranslation();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      accountType: 'individual',
      acceptTerms: false,
    },
  });

  const selectedAccountType = watch('accountType');

  const onSubmit = async (data: SignupFormData) => {
    setError('');
    setSuccess('');

    // Get reCAPTCHA token
    let captchaToken: string | undefined;
    if (executeRecaptcha) {
      try {
        captchaToken = await executeRecaptcha('signup');
      } catch (e) {
        setError(t('auth:signup.captcha_failed'));
        return;
      }
    } else {
      // reCAPTCHA not available
    }

    const result = await signup(data.name, data.email, data.password, data.accountType, captchaToken);

    if (result.success) {
      setSuccess(t('auth:signup.account_created_success'));
      setTimeout(() => {
        onClose();
      }, 3000);
    } else {
      setError(result.error || t('auth:signup.error_creating_account'));
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setSuccess('');
    const result = await signInWithGoogle();

    if (!result.success) {
      setError(result.error || t('auth:signup.error_google_signin'));
    }
  };

  // HIG Styles
  const labelClass = "block text-sm font-medium text-hig-text-secondary mb-1.5";
  const inputClass = "w-full bg-hig-bg-tertiary border border-hig-border rounded-lg px-4 py-2.5 text-sm text-hig-text-primary placeholder-hig-text-tertiary focus:outline-none focus:ring-1 focus:ring-hig-accent focus:border-hig-accent transition-all";

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-hig-text-primary mb-2">{t('common:signup')}</h2>
        <p className="text-hig-text-secondary">Create your account and start now</p>
      </div>

      {error && (
        <div className="bg-hig-error/10 border border-hig-error/30 text-hig-error px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-hig-success/10 border border-hig-success/30 text-hig-success px-4 py-3 rounded-lg text-sm flex items-center gap-2">
          <CheckCircle size={16} />
          {success}
        </div>
      )}

      {/* Account Type Selection */}
      <div>
        <label className={labelClass}>
          {t('auth:signup.account_type')}
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className={`relative flex items-center p-3 border rounded-xl cursor-pointer transition-all ${selectedAccountType === 'individual'
            ? 'border-hig-accent bg-hig-accent/5'
            : 'border-hig-border hover:bg-hig-bg-tertiary'
            }`}>
            <input
              {...register('accountType')}
              type="radio"
              value="individual"
              className="sr-only"
            />
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${selectedAccountType === 'individual' ? 'bg-hig-accent text-white' : 'bg-hig-bg-tertiary text-hig-text-secondary'
                }`}>
                <User className="h-4 w-4" />
              </div>
              <span className="text-sm font-medium text-hig-text-primary">{t('auth:signup.personal_account')}</span>
            </div>
            {selectedAccountType === 'individual' && (
              <div className="absolute top-2 right-2 text-hig-accent">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="7" r="7" fill="currentColor" /><path d="M4 7L6 9L10 5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </div>
            )}
          </label>

          <label className={`relative flex items-center p-3 border rounded-xl cursor-pointer transition-all ${selectedAccountType === 'company'
            ? 'border-hig-accent bg-hig-accent/5'
            : 'border-hig-border hover:bg-hig-bg-tertiary'
            }`}>
            <input
              {...register('accountType')}
              type="radio"
              value="company"
              className="sr-only"
            />
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${selectedAccountType === 'company' ? 'bg-hig-accent text-white' : 'bg-hig-bg-tertiary text-hig-text-secondary'
                }`}>
                <Building className="h-4 w-4" />
              </div>
              <span className="text-sm font-medium text-hig-text-primary">{t('auth:signup.company_account')}</span>
            </div>
            {selectedAccountType === 'company' && (
              <div className="absolute top-2 right-2 text-hig-accent">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="7" r="7" fill="currentColor" /><path d="M4 7L6 9L10 5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </div>
            )}
          </label>
        </div>
      </div>

      {/* Google Sign In */}
      <button
        type="button"
        onClick={handleGoogleSignIn}
        disabled={isLoading}
        className="w-full flex items-center justify-center gap-3 bg-hig-bg-tertiary border border-hig-border text-hig-text-primary py-3 px-4 rounded-lg font-medium hover:bg-hig-bg-elevated disabled:opacity-50 transition-colors"
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
        </svg>
        {isLoading ? t('auth:login.connecting') : t('auth:login.continue_with_google')}
      </button>

      {/* Divider */}
      <div className="flex items-center gap-4">
        <div className="flex-1 h-px bg-hig-divider"></div>
        <span className="text-sm text-hig-text-tertiary">or</span>
        <div className="flex-1 h-px bg-hig-divider"></div>
      </div>

      <form onSubmit={handleSubmit(onSubmit, (validationErrors) => {
        // Show a generic error so the user knows something went wrong
        setError('Please check all fields are filled correctly.');
        // Scroll to first error if form validation fails silently
        setTimeout(() => {
          const firstError = document.querySelector('.text-hig-error');
          if (firstError) {
            firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      })} className="space-y-4">
        <div>
          <label htmlFor="name" className={labelClass}>
            {selectedAccountType === 'company' ? t('auth:signup.company_name') : t('auth:signup.full_name')}
          </label>
          <div className="relative group">
            <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-hig-text-tertiary pointer-events-none">
              {selectedAccountType === 'company' ? <Building size={16} /> : <User size={16} />}
            </div>
            <input
              {...register('name')}
              type="text"
              id="name"
              autoComplete="name"
              className={`${inputClass} pl-10`}
              placeholder={selectedAccountType === 'company' ? t('auth:signup.your_company_name') : t('auth:signup.john_doe')}
            />
          </div>
          {errors.name && <p className="mt-1 text-sm text-hig-error">{errors.name.message}</p>}
        </div>

        <div>
          <label htmlFor="email" className={labelClass}>
            {t('auth:signup.email')}
          </label>
          <div className="relative group">
            <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-hig-text-tertiary pointer-events-none">
              <Mail size={16} />
            </div>
            <input
              {...register('email')}
              type="email"
              id="email"
              autoComplete="email"
              className={`${inputClass} pl-10`}
              placeholder="you@example.com"
            />
          </div>
          {errors.email && <p className="mt-1 text-sm text-hig-error">{errors.email.message}</p>}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="password" className={labelClass}>
              {t('auth:signup.password')}
            </label>
            <div className="relative group">
              <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-hig-text-tertiary pointer-events-none">
                <Lock size={16} />
              </div>
              <input
                {...register('password')}
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="new-password"
                className={`${inputClass} pl-10 pr-10`}
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-hig-text-tertiary hover:text-hig-text-secondary"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {errors.password && <p className="mt-1 text-xs text-hig-error">{errors.password.message}</p>}
          </div>

          <div>
            <label htmlFor="confirmPassword" className={labelClass}>
              {t('auth:signup.confirm_password')}
            </label>
            <div className="relative group">
              <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-hig-text-tertiary pointer-events-none">
                <Lock size={16} />
              </div>
              <input
                {...register('confirmPassword')}
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                autoComplete="new-password"
                className={`${inputClass} pl-10 pr-10`}
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-hig-text-tertiary hover:text-hig-text-secondary"
              >
                {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {errors.confirmPassword && <p className="mt-1 text-xs text-hig-error">{errors.confirmPassword.message}</p>}
          </div>
        </div>

        {/* Terms of Service Checkbox */}
        <div className="mt-4">
          <label className="flex items-start gap-3 cursor-pointer group">
            <div className="relative mt-0.5">
              <input
                {...register('acceptTerms')}
                type="checkbox"
                className="sr-only"
                id="acceptTerms"
              />
              <div className={`w-5 h-5 border-2 rounded transition-colors flex items-center justify-center ${watch('acceptTerms') ? 'bg-hig-accent border-hig-accent' : 'border-hig-border bg-hig-bg-tertiary'}`}>
                {watch('acceptTerms') && (
                  <svg className="w-3 h-3 text-white" viewBox="0 0 12 12" fill="none">
                    <path d="M2 6L5 9L10 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </div>
            </div>
            <span className="text-sm text-hig-text-secondary leading-tight">
              I agree to the{' '}
              <a href="/privacy" target="_blank" className="text-hig-accent hover:underline">Privacy Policy</a>
            </span>
          </label>
          {errors.acceptTerms && <p className="mt-1 text-sm text-hig-error">{errors.acceptTerms.message}</p>}
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-2.5 mt-4 rounded-lg font-bold text-sm transition-opacity hover:opacity-90 disabled:opacity-50"
          style={{ background: 'var(--accent)', color: '#0F172A' }}
        >
          {isLoading ? t('auth:signup.creating') : t('auth:signup.create_my_account')}
        </button>
      </form>

      <div className="text-center">
        <p className="text-sm text-hig-text-secondary">
          {t('auth:signup.already_have_account')}{' '}
          <button
            type="button"
            onClick={onToggleMode}
            className="hover:underline font-medium"
            style={{ color: 'var(--accent)' }}
          >
            {t('auth:login.log_in')}
          </button>
        </p>
      </div>
    </div>
  );
};

export default SignupForm;